import { useContext, useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom'
import { useKeycloak } from '@react-keycloak/web'
import Logout from '@mui/icons-material/Logout'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import LightModeIcon from '@mui/icons-material/LightMode'
import PersonIcon from '@mui/icons-material/Person'
import { Box, Menu, Tooltip, Divider, MenuItem, IconButton, ListItemIcon, AppBar } from '@mui/material'
import Logo from '../../../assets/images/spectrum-logo-white.png'
import { AuthContext } from '../../../contexts/auth.context'
import { hasPermission } from '../../../utils/functions'
import { PlaybookContext } from '../../../contexts/playbooks.context'
import { config } from '../../../config'
import { AppConfigContext } from '../../../contexts/app-config.context'
import { HeaderLink, HeaderLinks } from '../../../utils/constants'

import styles from './styles.module.scss'

const Header = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [allowedLinks, setAllowedLinks] = useState<HeaderLink[]>([])

  const { user } = useContext(AuthContext)
  const { playbook, handleUpdate } = useContext(PlaybookContext)
  const { isLightMode, setIsLightMode } = useContext(AppConfigContext)

  const { keycloak } = useKeycloak()

  const open = Boolean(anchorEl)
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
    setIsOpen(true)
  }

  const handleClose = () => {
    setAnchorEl(null)
    setIsOpen(false)
  }

  const toggleMode = () => {
    window.localStorage.setItem('mode', isLightMode ? 'dark' : 'light')
    setIsLightMode(!isLightMode)
    if (playbook) handleUpdate(playbook)
  }

  const logoutWithRedirect = () => {
    setIsOpen(false)
    keycloak.logout()
  }

  useEffect(() => {
    if (user) {
      setAllowedLinks(
        HeaderLinks.filter((link) =>
          link.allowedFor.some(([action, resource_type]) => hasPermission(action, resource_type, user.roles)),
        ),
      )
    }
  }, [user])

  if (!keycloak.authenticated) {
    return null
  }

  return (
    <AppBar
      color="primary"
      data-testid="header"
      className={`${styles.header} header`}
      sx={{
        borderBottom: isLightMode ? 'none' : '1px solid rgba(255, 255, 255, 0.15)',
      }}
    >
      <Box className={styles.links}>
        <img src={Logo} className={styles.logo} alt="" role="img" />
        {allowedLinks
          .filter((l) => config.dashboardsEnabled || !l.dashboard)
          .map(({ url, title }) => (
            <NavLink className={({ isActive }) => (isActive ? styles.active : '')} key={title} to={url} role="link">
              {title}
            </NavLink>
          ))}
      </Box>
      <Box className={styles.rightSide} style={{ display: 'flex' }}>
        <Tooltip title={`Switch to ${isLightMode ? 'dark' : 'light'} mode`} disableInteractive>
          <IconButton className={styles.themeModeBtn} data-testid="themeModeBtn" onClick={toggleMode}>
            {isLightMode ? <DarkModeIcon /> : <LightModeIcon />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Account settings" disableInteractive>
          <IconButton
            onClick={handleClick}
            className={styles.accountBtn}
            aria-controls={open ? 'account-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
          >
            <PersonIcon />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={anchorEl}
          id="account-menu"
          open={isOpen}
          onClose={handleClose}
          onClick={handleClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          sx={{
            mt: 1.5,
          }}
        >
          <MenuItem onClick={handleClose}>
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            {keycloak.tokenParsed?.email}
          </MenuItem>
          <Divider />
          <MenuItem onClick={logoutWithRedirect}>
            <ListItemIcon>
              <Logout fontSize="small" />
            </ListItemIcon>
            Logout
          </MenuItem>
        </Menu>
      </Box>
    </AppBar>
  )
}

export default Header
